/**
 * E2E intent tag — module-level state set by the runner via deep link.
 *
 * The runner pushes the active intent name onto this module before
 * invoking the mobile-side code that triggers wire calls (via
 * `exp+fluencia://dev/set-intent?name=X`). The HTTP and socket
 * wrappers read it at request-build time and embed it in the outbound
 * request: HTTP gets `x-e2e-intent: <name>` header; socket emits get
 * `_e2eIntent: <name>` merged into the payload.
 *
 * Why module-level state vs. context propagation: every mobile code
 * path that originates a request would need to thread an intent
 * argument through arbitrary depth. Putting it in module scope is the
 * pragmatic shape — `__DEV__`-guarded so production builds tree-shake
 * the entire concern.
 *
 * Tag lifecycle:
 *  - Empty by default. Production runs always see `null`.
 *  - Runner sets it before each `intent.run()` (top-level + nested
 *    `ctx.do(...)`) via the deep-link handler in `devReset.ts`.
 *  - Reading happens at request-build time inside the axios
 *    interceptor and inside `emitEvent`. Read-once semantics — once
 *    embedded in the request/payload, the tag is fixed for that wire
 *    crossing regardless of later setIntent() calls.
 *
 * Production safety: the read sites are also `__DEV__`-guarded, but
 * this module's runtime cost is one variable + two function calls in
 * any build. Negligible.
 */

let currentIntent: string | null = null;

export const getCurrentIntent = (): string | null => currentIntent;

export const setCurrentIntent = (name: string | null): void => {
  currentIntent = name;
};
